"use client";

import { c as _c } from "react/compiler-runtime";
import { useCallback, useEffect, useMemo, useRef } from "react";
import { cx } from "@/lib/tailwind-utils";
import { useAIChatContext } from "./contexts";
import type { ChatMessage } from "./types";
interface GroupInfo {
  isFirstInGroup: boolean;
  isLastInGroup: boolean;
}
interface AIChatMessagesProps {
  children: (message: ChatMessage, index: number, groupInfo: GroupInfo) => React.ReactNode;
  className?: string;
  /** Optional content rendered after messages (e.g. streaming indicators, errors). */
  footer?: React.ReactNode;
}

/**
 * Scroll container that iterates over messages from AIChatContext
 * and renders each via the children render function.
 * Handles auto-scroll, grouping, streaming indicator, and error display.
 */
export function AIChatMessages(t0) {
  const $ = _c(23);
  const {
    children,
    className,
    footer
  } = t0;
  const {
    state
  } = useAIChatContext();
  const {
    messages,
    streamingText
  } = state;
  const scrollContainerRef = useRef(null);
  const contentRef = useRef(null);
  const isNearBottomRef = useRef(true);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => {
      const el = scrollContainerRef.current;
      if (!el) {
        return;
      }
      isNearBottomRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < 80;
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const handleScroll = t1;
  const lastMessageContent = messages.length > 0 ? messages[messages.length - 1].role === "assistant" ? messages[messages.length - 1].content : null : null;
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => {
      if (!isNearBottomRef.current) {
        return;
      }
      const container = scrollContainerRef.current;
      if (container && container.scrollHeight > container.clientHeight) {
        container.scrollTop = container.scrollHeight;
      }
    };
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== lastMessageContent || $[3] !== messages.length || $[4] !== streamingText) {
    t3 = [messages.length, lastMessageContent, streamingText];
    $[2] = lastMessageContent;
    $[3] = messages.length;
    $[4] = streamingText;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  useEffect(t2, t3);
  let t4;
  let t5;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = () => {
      const content = contentRef.current;
      if (!content || typeof ResizeObserver === "undefined") {
        return;
      }
      let animationFrame = null;
      const resizeObserver = new ResizeObserver(() => {
        if (!isNearBottomRef.current) {
          return;
        }
        if (animationFrame !== null) {
          cancelAnimationFrame(animationFrame);
        }
        animationFrame = requestAnimationFrame(() => {
          const container_0 = scrollContainerRef.current;
          if (container_0) {
            container_0.scrollTop = container_0.scrollHeight;
          }
          animationFrame = null;
        });
      });
      resizeObserver.observe(content);
      return () => {
        resizeObserver.disconnect();
        if (animationFrame !== null) {
          cancelAnimationFrame(animationFrame);
        }
      };
    };
    t5 = [];
    $[6] = t4;
    $[7] = t5;
  } else {
    t4 = $[6];
    t5 = $[7];
  }
  useEffect(t4, t5);
  let t6;
  if ($[8] !== messages) {
    const visible = messages.map(_temp).filter(_temp2);
    t6 = visible.map((t7, i) => {
      const {
        msg: msg_1,
        originalIdx: originalIdx_0
      } = t7;
      const prev = i > 0 ? visible[i - 1].msg : undefined;
      const next = i < visible.length - 1 ? visible[i + 1].msg : undefined;
      const isFirstInGroup = !prev || prev.role !== msg_1.role || prev.role === "system" || prev.isSummary;
      const isLastInGroup = !next || next.role !== msg_1.role || next.role === "system" || next.isSummary;
      return {
        msg: msg_1,
        idx: originalIdx_0,
        isFirstInGroup,
        isLastInGroup
      };
    });
    $[8] = messages;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  const groupedMessages = t6;
  let t7;
  if ($[10] !== className) {
    t7 = cx("min-h-0 flex-1 overflow-y-auto overflow-x-hidden p-4", className);
    $[10] = className;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  let t8;
  if ($[12] !== children || $[13] !== groupedMessages) {
    let t9;
    if ($[15] !== children) {
      t9 = t10 => {
        const {
          msg: msg_2,
          idx,
          isFirstInGroup: isFirstInGroup_0,
          isLastInGroup: isLastInGroup_0
        } = t10;
        return <div key={msg_2.id ?? `${msg_2.role}-${idx}`} className={cx(isFirstInGroup_0 ? "mt-2" : "mt-0.5", idx === 0 && "mt-0")}>{children(msg_2, idx, {
            isFirstInGroup: !!isFirstInGroup_0,
            isLastInGroup: !!isLastInGroup_0
          })}</div>;
      };
      $[15] = children;
      $[16] = t9;
    } else {
      t9 = $[16];
    }
    t8 = groupedMessages.map(t9);
    $[12] = children;
    $[13] = groupedMessages;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  let t9;
  if ($[17] !== footer || $[18] !== t8) {
    t9 = <div ref={contentRef} className="flex flex-col">{t8}{footer}</div>;
    $[17] = footer;
    $[18] = t8;
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  let t10;
  if ($[20] !== t7 || $[21] !== t9) {
    t10 = <div ref={scrollContainerRef} onScroll={handleScroll} className={t7}>{t9}</div>;
    $[20] = t7;
    $[21] = t9;
    $[22] = t10;
  } else {
    t10 = $[22];
  }
  return t10;
}
function _temp2(t0) {
  const {
    msg: msg_0
  } = t0;
  if (msg_0.role !== "assistant") {
    return true;
  }
  const content_0 = typeof msg_0.content === "string" ? msg_0.content : String(msg_0.content || "");
  return !!content_0 || !!msg_0.analyticsResult || (msg_0.analyticsSteps?.length ?? 0) > 0;
}
function _temp(msg, originalIdx) {
  return {
    msg,
    originalIdx
  };
}